// The app's state. The viewport is a renderer: the bridge (viewport/bridge.ts) turns changes here
// into commands, and its events come back through the actions below.

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { Catalogue } from '../data/catalogue.ts';
import type { ShownBrain } from '../data/brain.ts';
import { subtree } from '../data/catalogue.ts';
import { leavesOut, scenarioById, type ChoiceOption, type ScenarioId, type ScenePreset, type TimelineEvent } from '../scenarios/index.ts';
import type { LoadedRun, Mode, Orbit, Preset, SectionPlane, Target, Vec3 } from '../viewport/api.ts';
import type { NoViewport } from '../viewport/wgpu.ts';
import type { CommonHeader, IndexAnnotation, SimFormat } from '../data/sims/types.ts';
import type { Overlay } from '../data/sims/overlay.ts';
import type { LiveResult } from '../data/sims/live.ts';

export type EngineKind = 'wgpu' | 'mock';

export interface EngineState {
  status: 'starting' | 'ready' | 'error';
  kind: EngineKind | null;
  /** The viewport API it speaks (`ready.api`), e.g. '0.1'. */
  api?: string;
  /** The sim formats (`header.format`) it reads, when its `ready` lists them. */
  sims?: string[];
  /** The pack kinds it loads (`anatomy`, `connectome`), when its `ready` lists them. */
  packs?: string[];
  /** The surface spaces it places on a brain's HD cortex, when its `ready` lists them (#541). */
  maps?: string[];
  /** The sim layers it draws, when its `ready` lists them (0.3, #576): an entry ending in `:` covers its prefix. */
  layers?: string[];
  /** Sims it isn't drawing, by path, and why: it doesn't read the format, or it refused the sim. */
  undrawn?: Record<string, string>;
  adapter?: string;
  version?: string;
  error?: string;
  /** Why the wasm viewport didn't start where the page asked for it, so the 2D preview (the mock) draws. */
  fallback?: NoViewport;
  fps?: number;
  frameMs?: number;
}

export interface CameraState {
  orbit: Orbit;
  eyeMm: Vec3;
  fovYDeg: number;
  viewProj: Float32Array;
  width: number;
  height: number;
}

export type LabelDensity = 'few' | 'normal' | 'many';

/** A sim the scene is running on, in one of its slots. */
export interface SimSlot {
  slot: string;
  label?: string;
  path: string;
  format: SimFormat;
  header: CommonHeader;
  /** Not a real model output (illustrative, or named synth/synthetic/probe): the scene stays a preview. */
  standIn: boolean;
  /** For traces and Methods only; not sent to the viewport. */
  appOnly: boolean;
  /** Markers its reader found in the run's own files (a cells run's spikes and events.json); else the header's. */
  events?: TimelineEvent[];
  /** Its index row's label and Methods text, while the header is the one the row was written for. */
  annotation?: IndexAnnotation;
}

export interface SimsState {
  /** The scene these belong to; anything else is stale. */
  scenario: ScenarioId | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  slots: SimSlot[];
  /** Each choice's options, and whether a run exists for it. */
  options: Record<string, (ChoiceOption & { available: boolean })[]>;
  /** The value each choice resolved to: the one chosen, or the nearest with a run. */
  values?: Record<string, string>;
  /** Template values the chosen options fix over the slice's (v5's one collateral grade). */
  fixed?: Record<string, string>;
  error?: string;
}

/** The scene's live solver session (app/live.ts runs the solves; the clot itself is in `stroke`). */
export interface LiveState {
  scenario: ScenarioId | null;
  /** `off`: the scene has none, or it isn't there. Only `ready` shows any of it. */
  status: 'off' | 'loading' | 'ready' | 'error';
  error?: string;
  /** The clot tool: a click on an artery moves the clot there (a click on the clotted one reopens it). */
  tool: boolean;
  /** The live solution on screen; null when live is off. */
  result: LiveResult | null;
  overlay: Overlay | null;
  /** Why the newest request didn't solve (a cut that leaves part of the network with no inlet or outlet),
   *  shown inline; the last result stays on screen. */
  note?: string;
}

/** Where a clot sits: a live solver vessel (its original segment id) and how far along it, 0.1–0.9 from its start. */
export interface Occlusion {
  vessel: string;
  s: number;
}

/**
 * The Stroke scene's controls; Blood flow uses the occlusion alone. app/live.ts solves the network
 * they describe, and app/sims.ts picks the tissue-fate run they name (app/stroke.ts says how).
 */
export interface StrokeState {
  /** The clot, or null when the vessels are open. */
  occlusion: Occlusion | null;
  /** The Circle of Willis collaterals (ACom, both PComs) in the live solve, and the tissue run's collateral grade (on: good, off: poor). */
  collaterals: boolean;
  /** Thrombectomy, hours after onset (1, 3 or 6); null for none. The live solve reopens the vessel from then. */
  thrombectomyH: number | null;
  /** Compare with ISLES'24: measured against modelled infarct once hemo publishes the comparison; the 3D overlay waits for volumes (viewport 0.3). */
  compare: boolean;
  /** What the arteries are coloured by (the Vessels section): the runs' and the live solve's; none keeps their own colours. */
  colourBy: 'velocity' | 'pressure' | 'none';
}

/** The Particles section (spec §15, Blood flow): whether the 1D runs' particles draw, and how many, as a
 *  share of the most the app asks for (data/sims/layers.ts `particleCount`). */
export interface ParticlesState {
  on: boolean;
  density: number;
}

/** The scene's pharma course, once app/sims.ts has read it: its own dose, and whether its series follow
 *  the model the Dose slider rescales by (data/sims/dose.ts `courseFits`). */
export interface DrugState {
  run: string;
  dose: number;
  unit: string;
  fits: boolean;
}

/** Scene sections' switches (spec §15): the cortex activity, and the electrode sites drawn over the picture:
 *  the model's scalp EEG and depth contacts, which its views set, and a real patient's own contacts, which
 *  are on until switched off. */
export interface SceneToggles {
  activity: boolean;
  eeg: boolean;
  seeg: boolean;
  contacts: boolean;
}

/** What the trace strip shows, where the scene offers a choice. */
export interface TraceView {
  /** Hemo: the segment shown; unset follows the selection, else the scene's default vessel. */
  segment?: string;
  /** Live: the solver vessel shown, by the same rule. */
  vessel?: string;
  /** Dynamics: scalp or depth electrodes, and which group of channels. */
  montage?: 'eeg' | 'seeg';
  group?: string;
  /** Pharma: the receptor target coloured in 3D and picked out in the curves. */
  target?: string;
  /** Pharma, a PK course: over its clock, or on the binding curves. */
  drugView?: 'time' | 'binding';
  /** Cells, a microcircuit: the cell types (the run's `type`) its Cells checkboxes leave out of the raster. */
  hiddenTypes?: string[];
}

export interface AtlasState {
  scenario: ScenarioId;
  preset: string | null;
  /** The view's own pack when it has one (a tissue pack), loaded instead of the scene's; the scene's sims aren't drawn on it. */
  viewPack: string | null;
  /** A view whose own pack the viewport couldn't load, and why: the scene went back to its own pack. */
  viewFailed: { pack: string; why: string } | null;
  engine: EngineState;
  packId: string | null;
  catalogue: Catalogue | null;
  /** The HD cortex the viewport draws over the pack (BigBrain's over the template), and the meshes it places surface runs from. */
  brain: ShownBrain | null;
  /** The pack load, and what its reply says: the surface spaces the loaded brain places (`maps`, #541), and a
   *  connectome pack's activity runs and the one the viewport plays (`runs`, `run`). */
  load: { status: 'idle' | 'loading' | 'ready' | 'error'; error?: string; loaded?: number; total?: number; maps?: string[]; runs?: LoadedRun[]; run?: string | null };
  /** A section's first read of the pack's volumes while it runs (viewport `progress` of 'volumes'); else null. */
  reading: { loaded: number; total: number } | null;

  selected: string | null;
  /** What's under the pointer in the viewport, or hovered in a list. */
  hovered: string | null;
  hoverFrom: 'viewport' | 'ui' | null;
  query: string;
  /** Search matches shown in 3D while the search is open; null when it isn't. */
  matches: string[] | null;
  layers: Record<string, Mode>;
  overrides: Record<string, Mode>;
  isolate: Target | null;
  section: SectionPlane[];
  explode: number;
  labelsOn: boolean;
  /** How many callouts: few (6), normal (6 at rest, 12 with a selection, isolation or matches), many (24). */
  labelDensity: LabelDensity;

  /** The scene's choices (drug, collateral grade…); unset ones take their defaults. */
  choices: Record<string, string>;
  sims: SimsState;
  /** Per-structure overlays the viewport colours by (from the sims on screen). */
  overlays: Overlay[];
  live: LiveState;
  stroke: StrokeState;
  tracesOpen: boolean;
  traceView: TraceView;
  toggles: SceneToggles;
  particles: ParticlesState;
  drug: DrugState | null;
  /** The Dose slider's mass, in the course's unit; null for the course's own dose. */
  drugDose: number | null;

  /** Scenario time in seconds; the clock (app/clock.ts) advances it while playing. */
  time: number;
  playing: boolean;
  speed: number;
  /** Playing at the timeline's `pacing` (a clip's slow motion) rather than at `speed`. */
  paced: boolean;
  /** The scene's clock while a view on its own pack plays that pack's run on its own: it comes back with the scene's views. */
  sceneClock: { time: number; speed: number; paced: boolean } | null;

  panels: { left: boolean; right: boolean };
  /** Presentation: every panel hidden, the scene and its caption only. */
  presenting: boolean;
  /** CSS px of the viewport covered by panels, so framing fits the clear area. */
  insets: { top: number; right: number; bottom: number; left: number };
  paletteOpen: boolean;
  helpOpen: boolean;
  methodsOpen: boolean;
  /** A camera move the bridge sends next (presets, focus, shortcuts). */
  cameraRequest: { seq: number; preset?: Preset; frame?: Target | 'visible'; direction?: Preset; orbit?: Orbit } | null;
}

export interface AtlasActions {
  setScenario(id: ScenarioId): void;
  applyPreset(p: ScenePreset): void;
  /** The view's own pack didn't load (`why`): back to the scene's first view on its own pack, noted for the HUD. */
  viewUnavailable(pack: string, why: string): void;
  setEngine(e: Partial<EngineState>): void;
  setCatalogue(packId: string, c: Catalogue, brain?: ShownBrain | null): void;
  setLoad(l: AtlasState['load']): void;
  select(id: string | null): void;
  hover(id: string | null, from: 'viewport' | 'ui'): void;
  setQuery(q: string, matches: string[] | null): void;
  setLayer(system: string, mode: Mode): void;
  setOverride(id: string, mode: Mode | null): void;
  isolateTarget(t: Target | null): void;
  /** The Layers head's "Show all": clears the isolation and shows every system and hidden structure. */
  showAll(): void;
  setSection(planes: SectionPlane[]): void;
  setExplode(v: number): void;
  toggleLabels(): void;
  setLabelDensity(d: LabelDensity): void;
  setChoice(id: string, value: string): void;
  setSims(s: SimsState): void;
  setOverlays(o: Overlay[]): void;
  setLive(l: Partial<LiveState>): void;
  /** Put the clot in a vessel (keeping its position unless given), or reopen with null. */
  setOcclusion(o: { vessel: string; s?: number } | null): void;
  /** Move the clot along its vessel (clamped to 0.1–0.9); nothing without one. */
  setClotPosition(s: number): void;
  setCollaterals(on: boolean): void;
  setThrombectomy(hours: number | null): void;
  setCompare(on: boolean): void;
  setColourBy(c: StrokeState['colourBy']): void;
  setParticles(p: Partial<ParticlesState>): void;
  setDrug(d: DrugState | null): void;
  /** Between a tenth and twice the course's own dose; null for its own. */
  setDrugDose(m: number | null): void;
  setTracesOpen(open: boolean): void;
  setTraceView(v: Partial<TraceView>): void;
  setToggle(k: keyof SceneToggles, on: boolean): void;
  setTime(t: number): void;
  setPlaying(p: boolean): void;
  setSpeed(s: number): void;
  setPaced(on: boolean): void;
  togglePanel(which: 'left' | 'right'): void;
  setPresenting(on: boolean): void;
  setInsets(i: AtlasState['insets']): void;
  setPalette(open: boolean): void;
  setHelp(open: boolean): void;
  setMethods(open: boolean): void;
  camera(req: Omit<NonNullable<AtlasState['cameraRequest']>, 'seq'>): void;
  resetView(): void;
}

export type Store = AtlasState & AtlasActions;

let cameraSeq = 0;

export const NO_SIMS: SimsState = { scenario: null, status: 'idle', slots: [], options: {} };
export const NO_LIVE: LiveState = { scenario: null, status: 'off', tool: false, result: null, overlay: null };
export const NO_STROKE: StrokeState = { occlusion: null, collaterals: true, thrombectomyH: null, compare: false, colourBy: 'velocity' };
/** 60 % of the most particles: the 12,000 the scenes have drawn since #263. */
export const NO_PARTICLES: ParticlesState = { on: true, density: 0.6 };

/** A clot position the solver accepts: 0.1–0.9 along the vessel. */
export const clampClot = (s: number) => Math.min(0.9, Math.max(0.1, Number.isFinite(s) ? s : 0.5));

const initial = (
  scenario: ScenarioId,
): Pick<AtlasState, 'preset' | 'viewPack' | 'viewFailed' | 'selected' | 'hovered' | 'hoverFrom' | 'query' | 'matches' | 'layers' | 'overrides' | 'isolate' | 'section' | 'explode' | 'time' | 'playing' | 'sceneClock' | 'choices' | 'traceView' | 'toggles'> => {
  // The scene opens in its first view, as applyPreset sets it (the app's first scene has no setScenario),
  // at its opening moment: the view's own time, else the scene's (design's default state).
  const sc = scenarioById(scenario);
  const first = sc.presets[0];
  return {
    preset: first?.id ?? null,
    viewPack: first?.pack ?? null,
    viewFailed: null,
    selected: first?.select ?? null,
    hovered: null,
    hoverFrom: null,
    query: '',
    matches: null,
    layers: { ...(first?.layers ?? {}) },
    overrides: {},
    isolate: first?.isolate ?? null,
    section: first?.section ?? [],
    explode: 0,
    time: first?.time ?? sc.start ?? 0,
    playing: false,
    sceneClock: null,
    choices: {},
    traceView: {},
    toggles: { activity: true, eeg: first?.electrodes?.eeg ?? false, seeg: first?.electrodes?.seeg ?? false, contacts: true },
  };
};

export const useAtlas = create<Store>()(
  subscribeWithSelector((set, get) => ({
    scenario: 'explore',
    ...initial('explore'),
    engine: { status: 'starting', kind: null },
    packId: null,
    catalogue: null,
    brain: null,
    load: { status: 'idle' },
    reading: null,
    labelsOn: true,
    labelDensity: 'normal',
    sims: NO_SIMS,
    overlays: [],
    live: NO_LIVE,
    stroke: NO_STROKE,
    particles: NO_PARTICLES,
    drug: null,
    drugDose: null,
    tracesOpen: true,
    speed: 1,
    paced: false,
    panels: { left: true, right: true },
    presenting: false,
    insets: { top: 0, right: 0, bottom: 0, left: 0 },
    paletteOpen: false,
    helpOpen: false,
    methodsOpen: false,
    cameraRequest: null,

    setScenario(id) {
      if (id === get().scenario) return;
      const sc = scenarioById(id);
      set({
        scenario: id,
        ...initial(id),
        sims: { ...NO_SIMS, scenario: id, status: sc.sims?.length ? 'loading' : 'idle' },
        overlays: [],
        live: { ...NO_LIVE, scenario: id, status: sc.live ? 'loading' : 'off' },
        stroke: { ...NO_STROKE, occlusion: sc.live?.clot ? { vessel: sc.live.clot.vessel, s: clampClot(sc.live.clot.s) } : null },
        particles: NO_PARTICLES,
        drug: null,
        drugDose: null,
        speed: sc.timeline?.speeds.find((s) => s >= 1) ?? sc.timeline?.speeds.at(-1) ?? 1,
        paced: false,
      });
      const first = sc.presets[0];
      if (first) get().applyPreset(first);
    },
    applyPreset(p) {
      const s = get();
      const pack = p.pack ?? null;
      // A view on its own pack plays that pack's run on its own clock, from its start in real time (until its
      // timeline says what it offers, app/clock.ts): the scene's clock waits, and comes back with the scene's views.
      const clock = pack === s.viewPack ? {} : pack ? { sceneClock: s.sceneClock ?? { time: s.time, speed: s.speed, paced: s.paced }, time: 0, speed: 1, paced: false } : s.sceneClock ? { ...s.sceneClock, sceneClock: null } : {};
      set({
        preset: p.id,
        viewPack: pack,
        viewFailed: null,
        layers: { ...(p.layers ?? {}) },
        overrides: {},
        isolate: p.isolate ?? null,
        section: p.section ?? [],
        selected: p.select ?? s.selected,
        ...clock,
        ...(p.time !== undefined ? { time: p.time } : {}),
        ...(p.speed !== undefined ? { speed: p.speed, paced: false } : {}),
        ...(p.electrodes ? { toggles: { ...s.toggles, ...p.electrodes } } : {}),
      });
      get().camera(p.orbit ? { orbit: p.orbit } : { preset: p.camera, ...(p.isolate ? { frame: p.isolate, direction: p.camera } : {}) });
    },
    viewUnavailable(pack, why) {
      const s = get();
      if (s.viewPack !== pack) return;
      const back = scenarioById(s.scenario).presets.find((p) => !p.pack);
      if (back) get().applyPreset(back);
      else set({ viewPack: null });
      set({ viewFailed: { pack, why } });
    },
    setEngine(e) {
      set({ engine: { ...get().engine, ...e } });
    },
    setCatalogue(packId, c, brain = null) {
      set({ packId, catalogue: c, brain });
    },
    setLoad(l) {
      set({ load: l });
    },
    select(id) {
      const s = get();
      if (id === s.selected) return;
      // A new selection is followed by the flow trace, over any vessel picked before.
      const { segment: _s, vessel: _v, ...traceView } = s.traceView;
      set({ selected: id, traceView });
    },
    hover(id, from) {
      const s = get();
      if (s.hovered === id && s.hoverFrom === from) return;
      // A list hover doesn't steal the viewport's hover, and vice versa, until it ends.
      if (id === null && s.hoverFrom !== from) return;
      set({ hovered: id, hoverFrom: id === null ? null : from });
    },
    setQuery(q, matches) {
      set({ query: q, matches });
    },
    setLayer(system, mode) {
      set({ layers: { ...get().layers, [system]: mode }, preset: null });
    },
    setOverride(id, mode) {
      const next = { ...get().overrides };
      if (mode === null) delete next[id];
      else next[id] = mode;
      set({ overrides: next, preset: null });
    },
    isolateTarget(t) {
      set({ isolate: t, preset: null });
      if (t) get().camera({ frame: t });
    },
    showAll() {
      const s = get();
      const sc = scenarioById(s.scenario);
      const layers = Object.fromEntries((s.catalogue?.systems ?? []).filter((g) => !leavesOut(sc, g.system)).map((g) => [g.system, 'shown' as Mode]));
      const overrides = Object.fromEntries(Object.entries(s.overrides).filter(([, m]) => m !== 'hidden'));
      set({ isolate: null, layers, overrides, preset: null });
    },
    setSection(planes) {
      set({ section: planes, preset: null });
    },
    setExplode(v) {
      set({ explode: Math.min(1, Math.max(0, v)) });
    },
    toggleLabels() {
      set({ labelsOn: !get().labelsOn });
    },
    setLabelDensity(d) {
      set({ labelDensity: d });
    },
    setChoice(id, value) {
      // Another run is another course: its own dose until the slider moves.
      if (get().choices[id] !== value) set({ choices: { ...get().choices, [id]: value }, drugDose: null });
    },
    setSims(s) {
      set({ sims: s });
    },
    setOverlays(o) {
      set({ overlays: o });
    },
    setLive(l) {
      set({ live: { ...get().live, ...l } });
    },
    setOcclusion(o) {
      const st = get().stroke;
      // A clot moving to another vessel keeps its position; a new one starts where the scene's own does.
      const s = o?.s ?? st.occlusion?.s ?? scenarioById(get().scenario).live?.clot?.s ?? 0.5;
      set({ stroke: { ...st, occlusion: o ? { vessel: o.vessel, s: clampClot(s) } : null } });
    },
    setClotPosition(s) {
      const st = get().stroke;
      if (st.occlusion && st.occlusion.s !== clampClot(s)) set({ stroke: { ...st, occlusion: { ...st.occlusion, s: clampClot(s) } } });
    },
    setCollaterals(on) {
      set({ stroke: { ...get().stroke, collaterals: on } });
    },
    setThrombectomy(hours) {
      set({ stroke: { ...get().stroke, thrombectomyH: hours } });
    },
    setCompare(on) {
      set({ stroke: { ...get().stroke, compare: on } });
    },
    setColourBy(c) {
      set({ stroke: { ...get().stroke, colourBy: c } });
    },
    setParticles(p) {
      const next = { ...get().particles, ...p };
      set({ particles: { on: next.on, density: Math.min(1, Math.max(0.05, Number.isFinite(next.density) ? next.density : NO_PARTICLES.density)) } });
    },
    setDrug(d) {
      const was = get().drug;
      set({ drug: d, ...(d?.run !== was?.run ? { drugDose: null } : {}) });
    },
    setDrugDose(m) {
      const d = get().drug;
      if (m === null || !d || !Number.isFinite(m)) return set({ drugDose: null });
      set({ drugDose: Math.min(2 * d.dose, Math.max(d.dose / 10, m)) });
    },
    setTracesOpen(open) {
      set({ tracesOpen: open });
    },
    setTraceView(v) {
      set({ traceView: { ...get().traceView, ...v } });
    },
    setToggle(k, on) {
      set({ toggles: { ...get().toggles, [k]: on } });
    },
    setTime(t) {
      set({ time: t });
    },
    setPlaying(p) {
      set({ playing: p });
    },
    setSpeed(s) {
      set({ speed: s, paced: false });
    },
    setPaced(on) {
      set({ paced: on });
    },
    togglePanel(which) {
      const panels = get().panels;
      set({ panels: { ...panels, [which]: !panels[which] } });
    },
    setPresenting(on) {
      set({ presenting: on });
    },
    setInsets(i) {
      const o = get().insets;
      if (o.top !== i.top || o.right !== i.right || o.bottom !== i.bottom || o.left !== i.left) set({ insets: i });
    },
    setPalette(open) {
      set({ paletteOpen: open });
    },
    setHelp(open) {
      set({ helpOpen: open });
    },
    setMethods(open) {
      set({ methodsOpen: open });
    },
    camera(req) {
      set({ cameraRequest: { ...req, seq: ++cameraSeq } });
    },
    resetView() {
      const sc = scenarioById(get().scenario);
      const first = sc.presets[0];
      set({ selected: null, query: '', matches: null });
      if (first) get().applyPreset(first);
    },
  })),
);

/** The structures an isolation keeps, for counts and the inspector. */
export function isolationMembers(s: Pick<AtlasState, 'catalogue' | 'isolate'>): Set<string> | null {
  const c = s.catalogue;
  const t = s.isolate;
  if (!c || !t) return null;
  if ('all' in t) return null;
  if ('id' in t) return new Set(subtree(c, t.id).map((x) => x.id));
  if ('ids' in t) return new Set(t.ids.flatMap((id) => subtree(c, id).map((x) => x.id)));
  if ('system' in t) return new Set(c.drawable.filter((x) => x.system === t.system && (!t.provenance || x.provenance === t.provenance)).map((x) => x.id));
  return null;
}
